import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, expand, map, of, reduce } from 'rxjs';
import { environment } from '../../../environments/environment';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import {
  AdminWeeklyReportFilters,
  ApiResponse,
  ApiWeeklyReportDto,
  BookedHoursApprovalBody,
  BookedHoursApprovalResult,
  ManualClockLogCreateItem,
  PayCodeListItem,
  PunchEventVm,
  PunchSource,
  PunchType,
  UserListItem,
  UserWonAssignment,
  UserWonOption,
  WeeklyReportBookedHoursDay,
  WeeklyReportDayLog,
  WeeklyReportDto,
  WeeklyReportTimeLogsRow,
  WeeklyReportUpdatedByUser,
} from '../models/admin-timesheet.model';

dayjs.extend(utc);
dayjs.extend(timezone);

@Injectable({ providedIn: 'root' })
export class AdminTimesheetService {
  private readonly baseUrl = environment.apiBaseUrl;

  constructor(private http: HttpClient) {}

  // ---------- Weekly report ----------
  getWeeklyReport(yyyyww: string, employeeId?: number | null): Observable<WeeklyReportDto> {
    return this.getWeeklyReportByFilters({
      fullYearWeek: yyyyww,
      employeeId: employeeId ?? null,
    });
  }

  getWeeklyReportByFilters(filters: AdminWeeklyReportFilters): Observable<WeeklyReportDto> {
    const params = this.buildWeeklyReportParams(filters);

    return this.http
      .get<{
        status: string;
        data: ApiWeeklyReportDto;
      }>(`${this.baseUrl}/api/employee-time-logs/weekly-report`, { params })
      .pipe(map((response) => this.normalizeWeeklyReport(response.data)));
  }

  private buildWeeklyReportParams(filters: AdminWeeklyReportFilters): HttpParams {
    let params = new HttpParams();

    const fullYearWeek = String(filters.fullYearWeek ?? '').trim();
    const searchTerm = String(filters.searchTerm ?? '').trim();

    if (fullYearWeek) {
      params = params.set('FullYearWeek', fullYearWeek);
    }

    if (typeof filters.companyId === 'number') {
      params = params.set('companyId', String(filters.companyId));
      params = params.set('companyIds', String(filters.companyId));
    }

    if (typeof filters.departmentId === 'number') {
      params = params.set('departmentId', String(filters.departmentId));
      params = params.set('departmentIds', String(filters.departmentId));
    }

    if (typeof filters.employeeId === 'number') {
      params = params.set('employeeId', String(filters.employeeId));
    }

    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }

    return params;
  }

  private emptyWeeklyReport(): WeeklyReportDto {
    return {
      payableHoursSync: null,
      timeLogs: [],
      bookedhours: [],
      userSchedules: [],
      invalidUserClockLogs: [],
      dayOffRequests: [],
    };
  }

  private normalizeWeeklyReport(api: ApiWeeklyReportDto | null | undefined): WeeklyReportDto {
    if (!api) {
      return this.emptyWeeklyReport();
    }

    const invalidByUserId = new Map<number, any[]>();

    for (const item of api.invalidUserClockLogs ?? []) {
      const userId = item?.user?.id;
      const invalidLogs = item?.user?.invalidUserClockLogs ?? [];

      if (!userId || !invalidLogs.length) {
        continue;
      }

      invalidByUserId.set(userId, invalidLogs);
    }

    const timeLogs = (api.timeLogs ?? []).map((item) => {
      const userWithLogs = item.user;
      const userId = userWithLogs.id;

      const rawLogs = userWithLogs.logs ?? [];
      const invalidLogs = invalidByUserId.get(userId) ?? [];

      const invalidByDate = new Map<string, any[]>();

      for (const invalidLog of invalidLogs) {
        const date = this.getClockLogDisplayDate(invalidLog.logTime, invalidLog.timeZoneId);

        if (!date) {
          continue;
        }

        if (!invalidByDate.has(date)) {
          invalidByDate.set(date, []);
        }

        invalidByDate.get(date)!.push(invalidLog);
      }

      const logs = rawLogs.map((day) => ({
        ...day,
        date: this.toDateKey(day.date),
        timeLogs: day.timeLogs ?? [],
        invalidUserClockLogs: invalidByDate.get(this.toDateKey(day.date)) ?? [],
      }));

      const existingDates = new Set(logs.map((day) => day.date));

      for (const [date, invalidForDate] of invalidByDate.entries()) {
        if (!existingDates.has(date)) {
          logs.push({
            date,
            timeLogs: [],
            invalidUserClockLogs: invalidForDate,
          });
        }
      }

      logs.sort((a, b) => a.date.localeCompare(b.date));

      const totalHours = this.sumClockedHours(logs);
      const { logs: _nestedLogs, ...user } = userWithLogs;

      return {
        user,
        logs,
        totalHours,
      };
    });

    const bookedhours = (api.bookedhours ?? []).map((item) => {
      const userWithBookedHours = item.user;

      const bookedHours: WeeklyReportBookedHoursDay[] = (userWithBookedHours.bookedHours ?? []).map(
        (day) => {
          const entries = day.bookedHours ?? day.bookedHour ?? [];

          return {
            date: this.toDateKey(day.date),
            bookedHours: entries,
            bookedHour: entries,
          };
        },
      );

      const calculatedTotalHours = this.sumBookedHours(bookedHours);
      const totalHours =
        typeof item.totalHours === 'number' ? this.round2(item.totalHours) : calculatedTotalHours;

      const { bookedHours: _nestedBookedHours, ...user } = userWithBookedHours;

      return {
        user,
        bookedHours,
        totalHours,
      };
    });

    const userSchedules = (api.userSchedules ?? []).map((row) => {
      const { schedules = [], ...user } = row.user;

      return {
        user,
        schedules,
      };
    });

    return {
      payableHoursSync: api.payableHoursSync ?? null,
      timeLogs,
      bookedhours,
      userSchedules,
      invalidUserClockLogs: api.invalidUserClockLogs ?? [],
      dayOffRequests: api.dayOffRequests ?? [],
    };
  }

  private getClockLogDisplayDate(
    logTime: string | null | undefined,
    timeZoneId: string | null | undefined,
  ): string | null {
    const raw = String(logTime ?? '').trim();

    if (!raw) {
      return null;
    }

    const tz = this.safeTz(timeZoneId ?? null);
    const parsed = this.hasOffsetOrZ(raw) ? dayjs(raw).tz(tz) : dayjs.utc(raw).tz(tz);

    if (!parsed.isValid()) {
      return raw.slice(0, 10) || null;
    }

    return parsed.format('YYYY-MM-DD');
  }

  private toDateKey(value: string | null | undefined): string {
    return String(value ?? '').slice(0, 10);
  }

  private sumClockedHours(days: WeeklyReportDayLog[]): number {
    let total = 0;

    for (const day of days ?? []) {
      for (const timeLog of day.timeLogs ?? []) {
        const totalHours = Number(timeLog?.totalHours ?? 0);

        if (Number.isFinite(totalHours) && totalHours > 0) {
          total += totalHours;
        }
      }
    }

    return this.round2(total);
  }

  private sumBookedHours(days: WeeklyReportBookedHoursDay[]): number {
    let total = 0;

    for (const day of days ?? []) {
      const entries = day.bookedHours ?? day.bookedHour ?? [];

      for (const bookedHour of entries) {
        total += typeof bookedHour?.hours === 'number' ? bookedHour.hours : 0;
      }
    }

    return this.round2(total);
  }

  private round2(value: number): number {
    return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
  }

  // ---------- Punch editing ----------
  createManualClockLogs(items: ManualClockLogCreateItem[]) {
    return this.http.post<{ status: string }>(`${this.baseUrl}/api/user-clock-logs/multiple`, items);
  }

  invalidateClockLog(clockLogId: number) {
    return this.http.patch<{ status: string }>(
      `${this.baseUrl}/api/user-clock-logs/${encodeURIComponent(String(clockLogId))}/invalid`,
      null,
    );
  }

  // ---------- Punch helpers ----------
  findEmployeeTimeLogsRow(report: WeeklyReportDto, userId: number): WeeklyReportTimeLogsRow | null {
    const rows = report?.timeLogs ?? [];

    return rows.find((row) => row.user?.id === userId) ?? null;
  }

  findDayLog(row: WeeklyReportTimeLogsRow, yyyyMmDd: string): WeeklyReportDayLog | null {
    const logs = row?.logs ?? [];

    return logs.find((day) => day.date === yyyyMmDd) ?? null;
  }

  buildPunchEventsForDay(dayLog: WeeklyReportDayLog | null): PunchEventVm[] {
    const timeLogs = dayLog?.timeLogs ?? [];
    const invalidLogs = Array.isArray(dayLog?.invalidUserClockLogs)
      ? dayLog.invalidUserClockLogs
      : [];

    const events: PunchEventVm[] = [];

    for (const timeLog of timeLogs) {
      if (timeLog.clockIn) {
        events.push(
          this.toPunchEvent({
            clockLogId: timeLog.clockIn.id,
            logTimeUtc: timeLog.clockIn.logTime,
            timeZoneId: timeLog.clockIn.timeZoneId ?? null,
            deviceName: timeLog.clockIn.clockDevice?.name ?? null,
            type: 'In',
            updatedAtUtc: timeLog.clockIn.updatedAt ?? null,
            updatedBy: timeLog.clockIn.updatedBy ?? null,
            isInvalid: false,
          }),
        );
      }

      if (timeLog.clockOut) {
        events.push(
          this.toPunchEvent({
            clockLogId: timeLog.clockOut.id,
            logTimeUtc: timeLog.clockOut.logTime,
            timeZoneId: timeLog.clockOut.timeZoneId ?? timeLog.clockIn?.timeZoneId ?? null,
            deviceName: timeLog.clockOut.clockDevice?.name ?? null,
            type: 'Out',
            updatedAtUtc: timeLog.clockOut.updatedAt ?? null,
            updatedBy: timeLog.clockOut.updatedBy ?? null,
            isInvalid: false,
          }),
        );
      }
    }

    for (const invalid of invalidLogs) {
      events.push(
        this.toPunchEvent({
          clockLogId: invalid.id,
          logTimeUtc: invalid.logTime,
          timeZoneId: invalid.timeZoneId ?? null,
          deviceName: invalid.clockDevice?.name ?? null,
          type: (invalid.type as PunchType) ?? 'Out',
          updatedAtUtc: invalid.updatedAt ?? null,
          updatedBy: invalid.updatedBy ?? null,
          isInvalid: true,
        }),
      );
    }

    events.sort((a, b) => a.timestampLocal.valueOf() - b.timestampLocal.valueOf());

    return events;
  }

  computeTotalClockedHoursForDay(dayLog: WeeklyReportDayLog | null): number {
    const timeLogs = dayLog?.timeLogs ?? [];
    let totalHours = 0;

    for (const timeLog of timeLogs) {
      totalHours += Number(timeLog.totalHours ?? 0);
    }

    return this.round2(totalHours);
  }

  private toPunchEvent(args: {
    clockLogId: number;
    logTimeUtc: string;
    timeZoneId: string | null;
    deviceName: string | null;
    type: PunchType;
    updatedAtUtc: string | null;
    updatedBy: WeeklyReportUpdatedByUser | null;
    isInvalid: boolean;
  }): PunchEventVm {
    const tz = this.safeTz(args.timeZoneId);
    const local = dayjs.utc(args.logTimeUtc).tz(tz);

    const source: PunchSource = args.deviceName ? 'Device' : 'Manual';
    const updatedAtLocal = args.updatedAtUtc ? dayjs.utc(args.updatedAtUtc).tz(tz) : null;
    const updatedByDisplayName = this.buildUpdatedByDisplayName(args.updatedBy);

    return {
      id: args.clockLogId,
      type: args.type,
      source,
      deviceName: args.deviceName ?? null,
      timeZoneId: tz,
      timestampLocal: local,
      displayTime: local.format('h:mm A'),
      displayDate: local.format('YYYY-MM-DD'),

      updatedAtUtc: args.updatedAtUtc ?? null,
      updatedAtLocal,
      displayUpdatedAt: updatedAtLocal ? updatedAtLocal.format('MMM D, YYYY h:mm A') : null,
      updatedBy: args.updatedBy ?? null,
      updatedByDisplayName,
      hasUpdateAudit: !!updatedAtLocal || !!updatedByDisplayName,

      isInvalid: args.isInvalid,
    };
  }

  private buildUpdatedByDisplayName(user: WeeklyReportUpdatedByUser | null): string | null {
    if (!user) {
      return null;
    }

    const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();

    if (fullName) {
      return fullName;
    }

    if (user.employeeNumber) {
      return user.employeeNumber;
    }

    return null;
  }

  private safeTz(timeZoneId: string | null): string {
    const fallback = dayjs.tz.guess();

    if (!timeZoneId) {
      return fallback;
    }

    try {
      dayjs().tz(timeZoneId);
      return timeZoneId;
    } catch {
      return fallback;
    }
  }

  private hasOffsetOrZ(value: string): boolean {
    if (!value) {
      return false;
    }

    return /([zZ]|[+-]\d{2}:\d{2})$/.test(value);
  }

  // ---------- Users ----------
  private getUsersPage(
    pageNumber: number,
    pageSize: number,
  ): Observable<ApiResponse<UserListItem[]>> {
    const params = new HttpParams()
      .set('pageNumber', String(pageNumber))
      .set('pageSize', String(pageSize));

    return this.http.get<ApiResponse<UserListItem[]>>(`${this.baseUrl}/api/users`, { params });
  }

  getAllUsers(): Observable<UserListItem[]> {
    const pageSize = 200;

    return this.getUsersPage(1, pageSize).pipe(
      expand((response) => {
        const current = response.currentPage ?? 1;
        const total = response.totalPages ?? 1;

        return current >= total ? of() : this.getUsersPage(current + 1, pageSize);
      }),
      map((response) => response.data ?? []),
      reduce((all, page) => all.concat(page), [] as UserListItem[]),
    );
  }

  // ---------- User WONs ----------
  private getUserWonsPage(employeeId: number, pageNumber: number, pageSize: number) {
    const params = new HttpParams()
      .set('employeeId', String(employeeId))
      .set('pageSize', String(pageSize))
      .set('pageNumber', String(pageNumber));

    return this.http.get<ApiResponse<UserWonAssignment[]>>(`${this.baseUrl}/api/user-wons`, {
      params,
    });
  }

  getAllUserWons(employeeId: number): Observable<UserWonAssignment[]> {
    const pageSize = 200;

    return this.getUserWonsPage(employeeId, 1, pageSize).pipe(
      expand((response) => {
        const current = response.currentPage ?? 1;
        const total = response.totalPages ?? 1;

        return current >= total ? of() : this.getUserWonsPage(employeeId, current + 1, pageSize);
      }),
      map((response) => response.data ?? []),
      reduce((all, page) => all.concat(page), [] as UserWonAssignment[]),
    );
  }

  getAllUserWonsAsOptions(employeeId: number): Observable<UserWonOption[]> {
    return this.getAllUserWons(employeeId).pipe(
      map((assignments) => {
        const byId = new Map<number, UserWonOption>();

        for (const assignment of assignments ?? []) {
          const won = assignment.workOrderNumber;

          if (!won?.id || !won?.won) {
            continue;
          }

          byId.set(won.id, {
            workOrderNumberId: won.id,
            won: won.won,
          });
        }

        return Array.from(byId.values()).sort((x, y) => x.won.localeCompare(y.won));
      }),
    );
  }

  // ---------- Pay Codes ----------
  private getPayCodesPage(
    pageNumber: number,
    pageSize: number,
  ): Observable<ApiResponse<PayCodeListItem[]>> {
    const params = new HttpParams()
      .set('pageNumber', String(pageNumber))
      .set('pageSize', String(pageSize));

    return this.http.get<ApiResponse<PayCodeListItem[]>>(`${this.baseUrl}/api/pay-codes`, {
      params,
    });
  }

  getAllPayCodes(): Observable<PayCodeListItem[]> {
    const pageSize = 200;

    return this.getPayCodesPage(1, pageSize).pipe(
      expand((response) => {
        const current = response.currentPage ?? 1;
        const total = response.totalPages ?? 1;

        return current >= total ? of() : this.getPayCodesPage(current + 1, pageSize);
      }),
      map((response) => response.data ?? []),
      reduce((all, page) => all.concat(page), [] as PayCodeListItem[]),
    );
  }

  // ---------- Booked Hours CRUD ----------
  createBookedHours(payload: {
    employeeId: number;
    hours: number;
    bookedDate: string;
    workOrderNumberId: number;
    payCodeId: number;
  }) {
    return this.http.post<{ status: string }>(`${this.baseUrl}/api/booked-hours`, payload);
  }

  patchBookedHoursHours(id: number, hours: number) {
    const safe = Number.isFinite(hours) ? Number(hours) : 0;

    return this.http.patch<{ status: string }>(
      `${this.baseUrl}/api/booked-hours/${id}/hours/${encodeURIComponent(String(safe))}`,
      null,
    );
  }

  // ---------- Booked Hours Approval ----------
  patchBookedHoursApproval(
    bookedHourId: number,
    body: BookedHoursApprovalBody,
  ): Observable<BookedHoursApprovalResult> {
    return this.http.patch<BookedHoursApprovalResult>(
      `${this.baseUrl}/api/booked-hours/${encodeURIComponent(String(bookedHourId))}/approval`,
      body,
    );
  }

  approveBookedHour(bookedHourId: number): Observable<BookedHoursApprovalResult> {
    return this.patchBookedHoursApproval(bookedHourId, {
      isApproved: true,
    });
  }

  requestBookedHourChange(
    bookedHourId: number,
    newBookedHours: number,
  ): Observable<BookedHoursApprovalResult> {
    return this.patchBookedHoursApproval(bookedHourId, {
      newBookedHours,
    });
  }

  approveBookedHourWithChange(
    bookedHourId: number,
    newBookedHours: number,
  ): Observable<BookedHoursApprovalResult> {
    return this.patchBookedHoursApproval(bookedHourId, {
      newBookedHours,
      isApproved: true,
    });
  }
}